import { Button, Pagination, Typography, Stack, Select, MenuItem, ListItemText, IconButton } from "@mui/material";
import {
  gridPageCountSelector,
  gridPageSelector,
  gridRowCountSelector,
  gridPaginationRowRangeSelector,
  useGridApiContext,
  useGridSelector,
  GridDensity,
  gridPageSizeSelector,
  gridDensitySelector,
} from "@mui/x-data-grid-pro";
import React, { useEffect } from "react";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { cacheService } from "../../../Core/Api/services";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";

interface TablePaginationProps {
  densityControl?: boolean;
  serverSidePagination: boolean;
}

const TablePagination: React.FC<TablePaginationProps> = (props) => {
  const { t } = useTranslation();
  const { serverSidePagination } = props;
  const apiRef = useGridApiContext();
  const location = useLocation();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const density = useGridSelector(apiRef, gridDensitySelector);
  const paginationRowRange = useGridSelector(apiRef, gridPaginationRowRangeSelector);
  const cachedDensity = cacheService.GetDensity(location.pathname);
  const cachedRowPerPage = cacheService.GetRowsPerPage(location.pathname);
  const handleChange = (event: React.ChangeEvent<unknown>, page: number) => {
    apiRef.current.setPage(page - 1);
  };

  const handleSkipPreviousClick: React.MouseEventHandler = () => {
    apiRef.current.setPage(0);
  };

  const handleNavigateBeforeClick: React.MouseEventHandler = () => {
    apiRef.current.setPage(page - 1);
  };

  const handleNavigateNextClick: React.MouseEventHandler = () => {
    apiRef.current.setPage(page + 1);
  };

  const handleSkipNextClick: React.MouseEventHandler = () => {
    apiRef.current.setPage(pageCount - 1);
  };

  const handlePageSizeChangeClick = (e) => {
    changeRowsPerPage(e.target.value as number);
  };

  useEffect(() => {
    if (!props.densityControl) return;

    cachedDensity && changeDensity(cachedDensity);
  }, [props.densityControl]);

  useEffect(() => {
    if (cachedRowPerPage) changeRowsPerPage(cachedRowPerPage);
  }, [cachedRowPerPage]);

  const rowPerPageOptions = [10, 20, 50, 100, 200, 500];

  const changeDensity = (newValue: GridDensity) => {
    apiRef.current.setDensity(newValue);
    cacheService.SetDensity(newValue, location.pathname);
  };

  const changeRowsPerPage = (newValue: number) => {
    apiRef.current.setPageSize(newValue);
    cacheService.SetRowsPerPage(newValue, location.pathname);
  };

  return (
    <>
      <Stack
        px={2}
        py={2}
        direction="row"
        sx={{ width: "100%" }}
        flex={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Pagination
          count={serverSidePagination ? Math.floor(rowCount / pageSize) + 1 : pageCount}
          page={page + 1}
          siblingCount={1}
          boundaryCount={1}
          hidePrevButton
          hideNextButton
          onChange={handleChange}
        />
        <Stack direction="row" alignItems="center">
          {serverSidePagination ? (
            <Typography color="text.tier">
              {page * pageSize + 1} - {Math.min(rowCount, (page + 1) * pageSize)} of {rowCount}
            </Typography>
          ) : (
            <>
              {paginationRowRange && (
                <Typography color="text.tier">
                  {paginationRowRange?.firstRowIndex + 1} - {paginationRowRange?.lastRowIndex + 1} of {rowCount}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Stack alignItems={"center"} direction="row">
          {props.densityControl && (
            <Select
              size="small"
              sx={{ maxWidth: "112px", height: "100%" }}
              inputProps={{ "aria-label": "Without label" }}
              value={density}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => changeDensity(e.target.value as GridDensity)}
            >
              <MenuItem value="compact">{t("common:compact")}</MenuItem>
              <MenuItem value="standard">{t("common:standard")}</MenuItem>
              <MenuItem value="comfortable">{t("common:comfortable")}</MenuItem>
            </Select>
          )}

          <Select
            IconComponent={ExpandMoreRoundedIcon}
            size="small"
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={pageSize}
            onChange={(e) => {
              handlePageSizeChangeClick(e);
            }}
            sx={{ mr: 2, ml: props.densityControl ? 1 : 0, height: 32 }}
          >
            {rowPerPageOptions.map((value) => (
              <MenuItem value={value} key={`page${value}`}>
                <ListItemText>{value}</ListItemText>
              </MenuItem>
            ))}
          </Select>
          <Stack direction="row" gap={0.5}>
            <IconButton onClick={handleSkipPreviousClick} color="ghost" size="small">
              <SkipPreviousIcon />
            </IconButton>
            <IconButton onClick={handleNavigateBeforeClick} color="ghost" size="small">
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={handleNavigateNextClick} color="ghost" size="small">
              <NavigateNextIcon />
            </IconButton>
            <IconButton onClick={handleSkipNextClick} color="ghost" size="small">
              <SkipNextIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default TablePagination;
